import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import DomainStatus from './DomainStatus.jsx';
import { conflictText } from './DomainVerify.jsx';

const SIGHTING = {
  unknown: { tone: 'bad', label: 'Not yours?' },
  ours: { tone: 'good', label: 'Yours' },
  ignored: { tone: 'neutral', label: 'Ignored' },
  reported: { tone: 'bad', label: 'Reported as fake' },
};

const SIGHTING_ACTIONS = {
  ours: { title: 'Is this site yours?', confirm: 'Yes, it’s ours', text: (h) => `We’ll add ${h} to your company’s websites. Verify it to show the verified seal there.` },
  reported: { title: 'Mark as a fake?', confirm: 'Mark as fake', danger: true, text: (h) => `We’ll record ${h} as a fake copy of your site, so it can be reported and checks of it say so.` },
  ignored: { title: 'Ignore this site?', confirm: 'Ignore', text: (h) => `We’ll stop flagging ${h}. Use this for test or preview sites that aren’t worth adding.` },
  unknown: { title: 'Flag it again?', confirm: 'Flag again', text: (h) => `${h} will be shown as unknown again.` },
};

export function CodeBlock({ code, label }) {
  return (
    <div className="code-block">
      <pre aria-label={label}><code>{code}</code></pre>
      <CopyButton text={code} />
    </div>
  );
}

export default function Website() {
  const { organisation: current } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [checking, setChecking] = useState(null);
  const [modal, setModal] = useState(null);   // { sighting, status }
  const base = current ? `/portal/organisations/${current.id}` : '';

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(`${base}/website`)); } catch (err) { setError(err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page"><PageHeader back={{ to: '/company', label: 'Company' }} title="Website protection" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const { organisation: org } = data;
  const unknown = data.sightings.filter((s) => s.status === 'unknown');

  const checkSite = async (d) => {
    setChecking(d.id); setNotice(null);
    try {
      const res = await api(`${base}/domains/${d.id}/check-website`, { method: 'POST' });
      if (res.conflict) setNotice({ tone: 'danger', text: conflictText(res.domain?.domain ? res.domain : d) });
      else if (res.found) setNotice({ tone: 'success', text: `Found it on ${res.url}. ${d.domain} is verified.` });
      else setNotice({ tone: 'warn', text: `${d.domain}: ${res.result}. If you’ve just added it, your site may need publishing or its cache clearing.` });
      await load();
    } catch (err) { setNotice({ tone: 'danger', text: err.message }); }
    setChecking(null);
  };

  const setBadgeEvent = async (id) => {
    try {
      await api(`${base}/website`, { method: 'PATCH', body: { badge_event_id: id ? Number(id) : null } });
      setData({ ...data, badge_event_id: id ? Number(id) : null });
      setNotice({ tone: 'success', text: 'Saved. The badge now checks against that event.' });
    } catch (err) { setNotice({ tone: 'danger', text: err.message }); }
  };

  return (
    <div className="page">
      <PageHeader back={{ to: '/company', label: 'Company' }} title="Website protection"
        subtitle="One line of code on your website proves it’s yours, warns you when someone copies it, and can show visitors a badge." />

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {unknown.length > 0 && (
        <Alert tone="danger" title={`Your code is running on ${unknown.length} site${unknown.length === 1 ? '' : 's'} that ${unknown.length === 1 ? 'isn’t' : 'aren’t'} yours`}>
          That usually means someone has copied your website. Check the list below.
        </Alert>
      )}

      <div className="grid-2">
        <Section title="1. Add this to every page" description="In your site builder (WordPress, Wix, Squarespace, Webflow…) paste it into the “custom code” or “header scripts” setting so it’s on every page. If someone edits your site’s code directly: just before </head>. It’s tiny, uses no cookies and stores nothing about your visitors.">
          <CodeBlock code={data.snippets.script} label="Website code" />
          <ul className="plain-list small">
            <li><strong>WordPress:</strong> a “header and footer scripts” plugin, or your theme&rsquo;s header settings.</li>
            <li><strong>Wix:</strong> Settings → Custom code → Add code, all pages, Head.</li>
            <li><strong>Squarespace:</strong> Settings → Advanced → Code injection → Header.</li>
            <li><strong>Google Tag Manager:</strong> a Custom HTML tag on All Pages.</li>
          </ul>
        </Section>

        <Section title="2. Show the badge (optional)" description="Put this where the badge should appear, e.g. your footer. It’s green on your own websites and turns into a warning on any copy of your site.">
          <CodeBlock code={data.snippets.badge} label="Badge code" />
          <div className="badge-previews">
            <figure><img src={`/api${base}/website/badge-preview/official.svg`} height="32" alt="Badge on your website" /><figcaption>On your sites</figcaption></figure>
            <figure><img src={`/api${base}/website/badge-preview/not_official.svg`} height="32" alt="Badge on a copy" /><figcaption>On a copy</figcaption></figure>
            <figure><img src={`/api${base}/website/badge-preview/unknown.svg`} height="32" alt="Badge when the browser doesn’t say" /><figcaption>When we can&rsquo;t tell</figcaption></figure>
          </div>
          <Field label="Clicking the badge checks the site on" as="select" value={data.badge_event_id || ''} onChange={(e) => setBadgeEvent(e.target.value)}
            disabled={!org.can_manage} hint="Visitors land on this event’s verify page with your site’s address filled in.">
            <option value="">Your next live event</option>
            {data.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}{ev.status !== 'live' ? ' (not live yet)' : ''}</option>)}
          </Field>
          {!data.events.some((ev) => ev.status === 'live') && (
            <Alert tone="warn">None of your events is live yet, so clicking the badge has nowhere to go. Put an event live on the Events page first.</Alert>
          )}
        </Section>
      </div>

      <Section title="3. Check your websites" description="Once the code is on your homepage, checking it proves the domain is yours — the same as a DNS record."
        flush={data.domains.length > 0}>
        {data.domains.length === 0 ? <EmptyState action={<Link className="btn btn-secondary" to="/company">Add your website</Link>}>Add your website on the company page first.</EmptyState> : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Domain</th><th>Ownership</th><th>Code last seen</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {data.domains.map((d) => (
                  <tr key={d.id}>
                    <td data-label="Domain" className="mono">{d.domain}</td>
                    <td data-label="Ownership"><DomainStatus status={d.status} />
                      {d.website_check_result && <div className="muted small">{d.website_check_result}</div>}</td>
                    <td data-label="Code last seen">{d.snippet_seen_at ? fmtDateTime(d.snippet_seen_at) : <span className="muted">Not yet</span>}</td>
                    <td className="right row-actions">
                      <button type="button" className="btn btn-small btn-secondary" disabled={checking === d.id || !org.can_manage} onClick={() => checkSite(d)}>
                        {checking === d.id ? 'Checking…' : 'Check my site'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Other sites your code has run on" description="Anywhere your code runs that isn’t one of your verified domains. A site you don’t recognise is almost always a copy of yours — we email your owners and admins the first time we see each one."
        flush={data.sightings.length > 0}>
        {data.sightings.length === 0 ? <EmptyState>None so far.</EmptyState> : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Site</th><th>First page</th><th>Seen</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {data.sightings.map((sg) => (
                  <tr key={sg.id}>
                    <td data-label="Site" className="mono">{sg.host}</td>
                    <td data-label="First page" className="mono small">{sg.first_path || '/'}</td>
                    <td data-label="Seen" className="small">{fmtDateTime(sg.first_seen)}{sg.hits > 1 ? ` · ${sg.hits} times` : ''}</td>
                    <td data-label="Status"><Pill tone={SIGHTING[sg.status].tone}>{SIGHTING[sg.status].label}</Pill></td>
                    <td className="right row-actions nowrap">
                      {org.can_manage && sg.status === 'unknown' && (
                        <>
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ sighting: sg, status: 'ours' })}>It&rsquo;s ours</button>
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ sighting: sg, status: 'ignored' })}>Ignore</button>
                          <button type="button" className="btn btn-small btn-danger-ghost" onClick={() => setModal({ sighting: sg, status: 'reported' })}>It’s a fake</button>
                        </>
                      )}
                      {org.can_manage && sg.status !== 'unknown' && (
                        <button type="button" className="link-button small" onClick={() => setModal({ sighting: sg, status: 'unknown' })}>Undo</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {modal && (
        <ConfirmModal title={SIGHTING_ACTIONS[modal.status].title} confirmLabel={SIGHTING_ACTIONS[modal.status].confirm}
          danger={SIGHTING_ACTIONS[modal.status].danger} onClose={() => setModal(null)}
          onConfirm={async () => {
            await api(`${base}/website/sightings/${modal.sighting.id}`, { method: 'PATCH', body: { status: modal.status } });
            setModal(null);
            await load();
          }}>
          <p>{SIGHTING_ACTIONS[modal.status].text(modal.sighting.host)}</p>
        </ConfirmModal>
      )}
    </div>
  );
}
